<script setup lang="ts">
const {
  bold,
  color = "inherit",
  size = ELEMENT_SIZE.medium,
} = defineProps<{
  /**
   * Whether the list item should be bold
   * @default false
   * @type {boolean}
   */
  bold?: boolean;
  /**
   * The color of the list item. Set by default to "inherit" to inherit the text color from the parent.
   * @default "inherit"
   * @type {BrandColor | "inherit"}
   */
  color?: BrandColor | "inherit";
  /**
   * The size of the list item
   * @default ELEMENT_SIZE.medium
   * @type {ElementSize}
   */
  size?: ElementSize;
}>();
</script>

<template>
  <li
    :class="[
      'foundation-list-item',
      `foundation-list-item--size-${size}`,
      `foundation-list-item--color-${color}`,
      {
        'foundation-list-item--bold': bold,
      },
    ]"
  >
    <div v-if="$slots.start" class="foundation-list-item__start">
      <slot name="start" />
    </div>
    <div class="foundation-list-item__content">
      <slot />
    </div>
    <div v-if="$slots.end" class="foundation-list-item__end">
      <slot name="end" />
    </div>
  </li>
</template>

<style scoped lang="scss">
:deep(p) {
  margin: 0;
}

.foundation-list-item {
  display: flex;
  align-items: center;
  container-type: inline-size;

  &__start,
  &__end {
    display: flex;
    flex: 0 0 auto;
    align-items: center;

    :deep(svg),
    :deep(.iconify) {
      width: 1.2em;
      height: 1.2em;
    }
  }

  &__content {
    flex: 1 1 auto;
  }

  /* Modifiers */
  &--bold {
    font-weight: var(--cm-font-weight-bold, 700);
    font-family: var(
      --cm-font-family-bold,
      var(--font-family-bold, sans-serif)
    );
  }

  /* Sizes */
  &--size {
    &-small {
      gap: var(--cm-spacing-2, 0.5rem);
      font-size: var(--cm-font-size-sm, 0.875rem);
      margin-block: var(--cm-spacing-1, 0.25rem);

      @container (width > 25rem) {
        gap: var(--cm-spacing-3, 0.75rem);
      }
    }

    &-medium {
      gap: var(--cm-spacing-2, 0.5rem);
      font-size: var(--cm-font-size-sm, 0.875rem);
      margin-block: var(--cm-spacing-3, 0.75rem);

      @container (width > 25rem) {
        gap: var(--cm-spacing-4, 1rem);
        font-size: var(--cm-font-size-md, 1rem);
      }
    }

    &-large {
      gap: var(--cm-spacing-3, 0.75rem);
      font-size: var(--cm-font-size-md, 1rem);
      margin-block: var(--cm-spacing-6, 1.5rem);

      @container (width > 25rem) {
        gap: var(--cm-spacing-4, 1rem);
        font-size: var(--cm-font-size-lg, 1.125rem);
      }
    }
  }

  /* Colors */
  &--color {
    &-inherit {
      color: inherit;
    }

    &-primary {
      color: var(--cm-color-primary, #ff0038);
    }

    &-black {
      color: var(--cm-color-black, #000);
    }

    &-white {
      color: var(--cm-color-white, #fff);
    }
  }
}
</style>
